import React, { useState } from 'react';

import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';

import { useBuildFlightCampaign, useValidateFlightCampaignBuild } from '~/hooks/apis/flights/useCampaignBuilder';
import { useGetCorridorOpportunity } from '~/hooks/apis/flights/useCorridorOpportunities';

import type { BuildFlightCampaignBody, BuildFlightCampaignResult, BuildPublishMode } from '~/services/FlightsService';

import type { CorridorOpportunity } from '~/types/corridorOpportunity';

import { FlightsCampaignsForm, OnSaveFormData } from '../FlightsCampaigns/FlightsCampaignsForm';

import CampaignPublication from './CampaignPublication';
import PreviewStep from './PreviewStep';
import { campaignFromOpportunity, getOpportunityBuildError, toOpportunityBuildBody } from './builderState';

export default function OpportunityBuildReview({ opportunity }: { opportunity: CorridorOpportunity }) {
  const [campaign, setCampaign] = useState<OnSaveFormData>(() =>
    opportunity.campaignDraft ? campaignFromOpportunity(opportunity) : undefined,
  );
  const [editingCampaign, setEditingCampaign] = useState(true);
  const [bookingClass, setBookingClass] = useState(opportunity.campaignDraft?.flightDeal.bookingClassCode ?? '');
  const [validatedBody, setValidatedBody] = useState<BuildFlightCampaignBody>();
  const [preview, setPreview] = useState<BuildFlightCampaignResult>();
  const [savedCampaignId, setSavedCampaignId] = useState<string>();
  const [error, setError] = useState<string>();
  const [checking, setChecking] = useState(false);
  const latestOpportunity = useGetCorridorOpportunity(opportunity.id);
  const validate = useValidateFlightCampaignBuild();
  const build = useBuildFlightCampaign();
  const busy = checking || validate.isPending || build.isPending;
  const reviewError = getOpportunityBuildError(opportunity);

  const validateDeal = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(undefined);
    setValidatedBody(undefined);
    setPreview(undefined);
    const stale = getOpportunityBuildError(opportunity);
    if (stale) return setError(stale);
    const body = toOpportunityBuildBody(opportunity, campaign, bookingClass);
    try {
      const response = await validate.mutateAsync(body);
      setPreview(response.result);
      if (response.status === 200) setValidatedBody(body);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'The draft could not be validated. Please retry.');
    }
  };

  const save = async (mode: BuildPublishMode) => {
    const stale = getOpportunityBuildError(opportunity);
    if (stale) return setError(stale);
    if (busy || !validatedBody) return;
    setError(undefined);
    setChecking(true);
    try {
      // Another reviewer or the cron may have saved or replaced this draft while the form was open.
      const latest = await latestOpportunity.refetch();
      if (latest.isError) throw latest.error;
      const latestError = getOpportunityBuildError(latest.data);
      if (latestError) throw new Error(latestError);
      if (
        latest.data.campaignDraft.sourceValidationId !== opportunity.campaignDraft.sourceValidationId ||
        latest.data.campaignDraft.generatedAt !== opportunity.campaignDraft.generatedAt
      ) {
        throw new Error('The generated draft changed. Review the refreshed campaign and confirm its booking class again.');
      }
      const response = await build.mutateAsync({ ...validatedBody, publish: mode });
      if (response.status === 200) setSavedCampaignId(response.result.campaign.id);
      else {
        setPreview(response.result);
        setValidatedBody(undefined);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'The draft could not be saved. Please retry.');
    } finally {
      setChecking(false);
    }
  };

  if (savedCampaignId) return <CampaignPublication campaignId={savedCampaignId} />;
  if (reviewError && !build.isPending) return <Alert severity="warning">{reviewError}</Alert>;

  if (editingCampaign) {
    return (
      <Stack gap={2}>
        <Alert severity="info">
          Review the campaign prepared by automation and add an approved destination image. The generated deal will be
          saved with it.
        </Alert>
        <FlightsCampaignsForm
          initialValues={campaign}
          submitLabel="Next: review deal"
          onSubmit={(value) => {
            setCampaign(value);
            setEditingCampaign(false);
          }}
        />
      </Stack>
    );
  }

  const deal = opportunity.campaignDraft.flightDeal;
  return (
    <Stack gap={3}>
      {error && <Alert severity="error">{error}</Alert>}
      <Typography variant="h6">
        {deal.originAirportCode} to {deal.destinationAirportCode} · {deal.carrierCode} · {deal.fareClass}
      </Typography>
      <Typography>{deal.dealDescription}</Typography>
      <Typography>
        Travel {deal.departureDate1} to {deal.arrivalDate1}. Standard price ${deal.perAdultPrice}
        {deal.luxPlusPerAdultPrice ? ` · Lux Plus $${deal.luxPlusPerAdultPrice}` : ''} per adult.
      </Typography>
      <Box component="form" onSubmit={validateDeal}>
        <Stack gap={2}>
          <TextField
            required
            label="Confirmed booking class"
            value={bookingClass}
            disabled={busy}
            helperText="Confirm the booking class from the validated fare before publication."
            onChange={(event) => {
              setBookingClass(event.target.value);
              setValidatedBody(undefined);
              setPreview(undefined);
            }}
          />
          <Stack direction="row" gap={2}>
            <Button type="submit" variant="contained" disabled={busy}>
              Validate and preview
            </Button>
            <Button
              disabled={busy}
              onClick={() => {
                setEditingCampaign(true);
                setValidatedBody(undefined);
                setPreview(undefined);
              }}
            >
              Back to campaign
            </Button>
          </Stack>
        </Stack>
      </Box>
      {preview && <PreviewStep preview={preview} busy={busy || !validatedBody} onSave={save} />}
    </Stack>
  );
}
